import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    Plus, Pencil, Trash2, X, AlertCircle, Package, Percent, Users,
} from 'lucide-react';
import { selectUser } from '../store/authSlice';
import { configApi } from '../api/configApi';

/**
 * Configuration: loan products, payees (identity/contact only), and the org's
 * base commission rates. Three tabs. Products and payees are simple CRUD over
 * a table + modal. Rates are a single per-org config (six rates) on one form.
 *
 * Rates are stored as decimal fractions (1% = 0.01) and shown as percent —
 * the form converts on the boundary so the user always types/reads percent.
 */
export default function ConfigurationPage() {
    const user = useSelector(selectUser);
    const orgUuid = user?.organisationUuid;
    const [tab, setTab] = useState('products');

    return (
        <div className="px-8 py-8 max-w-5xl">
            <div className="mb-6">
                <h2 className="font-display text-2xl font-semibold text-slate-900">
                    Configuration
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    Loan products, the payees you pay, and your base commission rates.
                </p>
            </div>

            <div className="flex gap-1 mb-6 border-b border-slate-200">
                <TabButton active={tab === 'products'} onClick={() => setTab('products')}
                           icon={<Package size={15} />} label="Products" />
                <TabButton active={tab === 'payees'} onClick={() => setTab('payees')}
                           icon={<Users size={15} />} label="Payees" />
                <TabButton active={tab === 'rates'} onClick={() => setTab('rates')}
                           icon={<Percent size={15} />} label="Rates" />
            </div>

            {tab === 'products' && <ProductsTab orgUuid={orgUuid} />}
            {tab === 'payees' && <PayeesTab orgUuid={orgUuid} />}
            {tab === 'rates' && <RatesTab orgUuid={orgUuid} />}
        </div>
    );
}

function TabButton({ active, onClick, icon, label }) {
    return (
        <button
            onClick={onClick}
            className={[
                'flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors',
                active
                    ? 'border-brand-600 text-brand-700 font-medium'
                    : 'border-transparent text-slate-500 hover:text-slate-800',
            ].join(' ')}
        >
            {icon}
            {label}
        </button>
    );
}

/* ─────────────────────────────  Products  ───────────────────────────── */

function ProductsTab({ orgUuid }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null); // product | {} (new) | null

    const reload = useCallback(() => {
        if (!orgUuid) return;
        setLoading(true);
        configApi.listProducts(orgUuid)
            .then(setProducts)
            .catch((e) => setError(apiError(e)))
            .finally(() => setLoading(false));
    }, [orgUuid]);

    useEffect(() => { reload(); }, [reload]);

    const onDelete = (p) => {
        if (!window.confirm(`Delete product "${p.name}"?`)) return;
        configApi.deleteProduct(orgUuid, p.uuid).then(reload).catch((e) => setError(apiError(e)));
    };

    return (
        <>
            <Toolbar
                title="Products"
                onAdd={() => setEditing({})}
                addLabel="Add product"
            />
            <ErrorBanner error={error} />
            <Card>
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                        <Th>Name</Th><Th>Code</Th><Th>Funder</Th>
                        <Th align="right">Upfront</Th><Th align="right">Trail p.a.</Th>
                        <Th align="right" />
                    </tr>
                    </thead>
                    <tbody>
                    {loading && <LoadingRow cols={6} />}
                    {!loading && products.length === 0 && <EmptyRow cols={6} label="No products yet." />}
                    {!loading && products.map((p) => (
                        <tr key={p.uuid} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                            <Td className="font-medium text-slate-900">{p.name}</Td>
                            <Td><Code>{p.code}</Code></Td>
                            <Td>{p.funder}</Td>
                            <Td align="right">{pct(p.funderUpfrontRate)}</Td>
                            <Td align="right">{pct(p.funderTrailRate)}</Td>
                            <Td align="right"><RowActions onEdit={() => setEditing(p)} onDelete={() => onDelete(p)} /></Td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </Card>

            {editing && (
                <ProductModal
                    orgUuid={orgUuid}
                    product={editing.uuid ? editing : null}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                    onError={setError}
                />
            )}
        </>
    );
}

function ProductModal({ orgUuid, product, onClose, onSaved, onError }) {
    const [form, setForm] = useState(() => ({
        name: product?.name ?? '',
        code: product?.code ?? '',
        funder: product?.funder ?? '',
        funderUpfrontPct: product ? toPct(product.funderUpfrontRate) : '',
        funderTrailPct: product ? toPct(product.funderTrailRate) : '',
    }));
    const [saving, setSaving] = useState(false);

    const submit = () => {
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            code: form.code.trim(),
            funder: form.funder.trim(),
            funderUpfrontRate: fromPct(form.funderUpfrontPct),
            funderTrailRate: fromPct(form.funderTrailPct),
        };
        const req = product
            ? configApi.updateProduct(orgUuid, product.uuid, payload)
            : configApi.createProduct(orgUuid, payload);
        req.then(onSaved).catch((e) => { onError(apiError(e)); setSaving(false); });
    };

    return (
        <Modal title={product ? 'Edit product' : 'Add product'} onClose={onClose}>
            <Field label="Name">
                <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} autoFocus />
            </Field>
            <Field label="Code" hint="Short code, unique per organisation">
                <Input value={form.code} onChange={(v) => setForm({ ...form, code: v })} />
            </Field>
            <Field label="Funder">
                <Input value={form.funder} onChange={(v) => setForm({ ...form, funder: v })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Upfront rate" hint="% received from funder">
                    <PctInput value={form.funderUpfrontPct} onChange={(v) => setForm({ ...form, funderUpfrontPct: v })} />
                </Field>
                <Field label="Trail rate p.a." hint="% received from funder">
                    <PctInput value={form.funderTrailPct} onChange={(v) => setForm({ ...form, funderTrailPct: v })} />
                </Field>
            </div>
            <ModalActions onCancel={onClose} onSave={submit} saving={saving} />
        </Modal>
    );
}

/* ──────────────────────────────  Payees  ────────────────────────────── */

function PayeesTab({ orgUuid }) {
    const [payees, setPayees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null);   // payee | {} | null

    const reload = useCallback(() => {
        if (!orgUuid) return;
        setLoading(true);
        configApi.listPayees(orgUuid)
            .then(setPayees)
            .catch((e) => setError(apiError(e)))
            .finally(() => setLoading(false));
    }, [orgUuid]);

    useEffect(() => { reload(); }, [reload]);

    const onDelete = (p) => {
        if (!window.confirm(`Delete payee "${p.name}"?`)) return;
        configApi.deletePayee(orgUuid, p.uuid).then(reload).catch((e) => setError(apiError(e)));
    };

    return (
        <>
            <Toolbar title="Payees" onAdd={() => setEditing({})} addLabel="Add payee" />
            <ErrorBanner error={error} />
            <Card>
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                        <Th>Name</Th><Th>Type</Th><Th>Office</Th><Th>Code</Th><Th>Contact</Th>
                        <Th align="right" />
                    </tr>
                    </thead>
                    <tbody>
                    {loading && <LoadingRow cols={6} />}
                    {!loading && payees.length === 0 && <EmptyRow cols={6} label="No payees yet." />}
                    {!loading && payees.map((p) => (
                        <tr key={p.uuid} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                            <Td className="font-medium text-slate-900">{p.name}</Td>
                            <Td><TypePill type={p.type} /></Td>
                            <Td>{p.office || <Muted>—</Muted>}</Td>
                            <Td><Code>{p.code}</Code></Td>
                            <Td>{p.contactName || <Muted>—</Muted>}</Td>
                            <Td align="right">
                                <RowActions onEdit={() => setEditing(p)} onDelete={() => onDelete(p)} />
                            </Td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </Card>

            {editing && (
                <PayeeModal
                    orgUuid={orgUuid}
                    payee={editing.uuid ? editing : null}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                    onError={setError}
                />
            )}
        </>
    );
}

function PayeeModal({ orgUuid, payee, onClose, onSaved, onError }) {
    const [form, setForm] = useState(() => ({
        type: payee?.type ?? 'SUBCONTRACTOR',
        name: payee?.name ?? '',
        office: payee?.office ?? '',
        code: payee?.code ?? '',
        contactName: payee?.contactName ?? '',
        contactEmail: payee?.contactEmail ?? '',
        contactPhone: payee?.contactPhone ?? '',
    }));
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        setSaving(true);
        const payload = {
            type: form.type,
            name: form.name.trim(),
            office: form.office.trim() || null,
            code: form.code.trim(),
            contactName: form.contactName.trim() || null,
            contactEmail: form.contactEmail.trim() || null,
            contactPhone: form.contactPhone.trim() || null,
        };
        try {
            if (payee) {
                await configApi.updatePayee(orgUuid, payee.uuid, payload);
            } else {
                await configApi.createPayee(orgUuid, payload);
            }
            onSaved();
        } catch (e) {
            onError(apiError(e));
            setSaving(false);
        }
    };

    return (
        <Modal title={payee ? 'Edit payee' : 'Add payee'} onClose={onClose}>
            <Field label="Type">
                <div className="flex gap-2">
                    {['SUBCONTRACTOR', 'REFERRER'].map((t) => (
                        <button
                            key={t}
                            onClick={() => setForm({ ...form, type: t })}
                            className={[
                                'flex-1 px-3 py-2 text-sm rounded-md border transition-colors',
                                form.type === t
                                    ? 'border-brand-500 bg-brand-50 text-brand-700 font-medium'
                                    : 'border-slate-300 text-slate-600 hover:border-slate-400',
                            ].join(' ')}
                        >
                            {t === 'SUBCONTRACTOR' ? 'Subcontractor' : 'Referrer'}
                        </button>
                    ))}
                </div>
            </Field>
            <Field label="Name">
                <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} autoFocus />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Office">
                    <Input value={form.office} onChange={(v) => setForm({ ...form, office: v })} />
                </Field>
                <Field label="Code" hint="Unique per organisation">
                    <Input value={form.code} onChange={(v) => setForm({ ...form, code: v })} />
                </Field>
            </div>
            <Field label="Contact name">
                <Input value={form.contactName} onChange={(v) => setForm({ ...form, contactName: v })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Contact email">
                    <Input type="email" value={form.contactEmail} onChange={(v) => setForm({ ...form, contactEmail: v })} />
                </Field>
                <Field label="Contact phone">
                    <Input value={form.contactPhone} onChange={(v) => setForm({ ...form, contactPhone: v })} />
                </Field>
            </div>
            <ModalActions onCancel={onClose} onSave={submit} saving={saving} />
        </Modal>
    );
}

/* ──────────────────────────────  Rates  ─────────────────────────────── */

/**
 * The org's single base rate config — six rates (funder / subcontractor /
 * referrer, each upfront + trail). One form, loaded once and saved as a whole.
 * GET returns null fields when nothing is saved yet, so the form starts blank.
 */
function RatesTab({ orgUuid }) {
    const [form, setForm] = useState(null);   // null until loaded
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        if (!orgUuid) return;
        let cancelled = false;
        setLoading(true);
        configApi.getRateConfig(orgUuid)
            .then((cfg) => {
                if (cancelled) return;
                setForm({
                    funderUpfrontPct:   cfg?.funderUpfrontRate   != null ? toPct(cfg.funderUpfrontRate)   : '',
                    funderTrailPct:     cfg?.funderTrailRate     != null ? toPct(cfg.funderTrailRate)     : '',
                    subbyUpfrontPct:    cfg?.subbyUpfrontRate    != null ? toPct(cfg.subbyUpfrontRate)    : '',
                    subbyTrailPct:      cfg?.subbyTrailRate      != null ? toPct(cfg.subbyTrailRate)      : '',
                    referrerUpfrontPct: cfg?.referrerUpfrontRate != null ? toPct(cfg.referrerUpfrontRate) : '',
                    referrerTrailPct:   cfg?.referrerTrailRate   != null ? toPct(cfg.referrerTrailRate)   : '',
                    subbyUpfrontRateColumn:    cfg?.subbyUpfrontRateColumn    ?? '',
                    subbyTrailRateColumn:      cfg?.subbyTrailRateColumn      ?? '',
                    referrerUpfrontRateColumn: cfg?.referrerUpfrontRateColumn ?? '',
                    referrerTrailRateColumn:   cfg?.referrerTrailRateColumn   ?? '',
                });
            })
            .catch((e) => !cancelled && setError(apiError(e)))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [orgUuid]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const save = () => {
        setSaving(true);
        setError(null);
        setSavedAt(null);
        const payload = {
            funderUpfrontRate:   fromPct(form.funderUpfrontPct   || '0'),
            funderTrailRate:     fromPct(form.funderTrailPct     || '0'),
            subbyUpfrontRate:    fromPct(form.subbyUpfrontPct    || '0'),
            subbyTrailRate:      fromPct(form.subbyTrailPct      || '0'),
            referrerUpfrontRate: fromPct(form.referrerUpfrontPct || '0'),
            referrerTrailRate:   fromPct(form.referrerTrailPct   || '0'),
            subbyUpfrontRateColumn:    form.subbyUpfrontRateColumn.trim()    || null,
            subbyTrailRateColumn:      form.subbyTrailRateColumn.trim()      || null,
            referrerUpfrontRateColumn: form.referrerUpfrontRateColumn.trim() || null,
            referrerTrailRateColumn:   form.referrerTrailRateColumn.trim()   || null,
        };
        configApi.saveRateConfig(orgUuid, payload)
            .then(() => setSavedAt(Date.now()))
            .catch((e) => setError(apiError(e)))
            .finally(() => setSaving(false));
    };

    return (
        <>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                    Base rates
                </h3>
            </div>
            <ErrorBanner error={error} />
            <Card>
                {loading || !form ? (
                    <p className="px-4 py-8 text-center text-sm text-slate-400">Loading…</p>
                ) : (
                    <div className="p-5 space-y-5">
                        <p className="text-sm text-slate-500">
                            The base commission rates applied to a run. The run screen
                            pre-fills from these and you can still adjust them per run.
                            Trail rates are per annum.
                        </p>

                        <RateRow
                            label="Funder"
                            hint="What you receive from the funder"
                            upfront={form.funderUpfrontPct}
                            trail={form.funderTrailPct}
                            onUpfront={(v) => set({ funderUpfrontPct: v })}
                            onTrail={(v) => set({ funderTrailPct: v })}
                        />
                        <RateRow
                            label="Subcontractor"
                            hint="What the subcontractor (office) receives"
                            upfront={form.subbyUpfrontPct}
                            trail={form.subbyTrailPct}
                            onUpfront={(v) => set({ subbyUpfrontPct: v })}
                            onTrail={(v) => set({ subbyTrailPct: v })}
                        />
                        <RateRow
                            label="Referrer"
                            hint="Paid out of the subcontractor's slice"
                            upfront={form.referrerUpfrontPct}
                            trail={form.referrerTrailPct}
                            onUpfront={(v) => set({ referrerUpfrontPct: v })}
                            onTrail={(v) => set({ referrerTrailPct: v })}
                        />

                        <div className="pt-3 border-t border-slate-100 space-y-3">
                            <div>
                                <p className="text-sm font-medium text-slate-800">
                                    Special-rate columns{' '}
                                    <span className="font-normal text-slate-400">(optional)</span>
                                </p>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    If the CRM file carries a per-loan rate in a column, name it
                                    here. When a loan has a non-zero value in that column, the run
                                    uses it for that loan instead of the rate above. Leave blank to
                                    always use the rates above.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Subcontractor upfront column">
                                    <Input
                                        value={form.subbyUpfrontRateColumn}
                                        onChange={(v) => set({ subbyUpfrontRateColumn: v })}
                                    />
                                </Field>
                                <Field label="Subcontractor trail column">
                                    <Input
                                        value={form.subbyTrailRateColumn}
                                        onChange={(v) => set({ subbyTrailRateColumn: v })}
                                    />
                                </Field>
                                <Field label="Referrer upfront column">
                                    <Input
                                        value={form.referrerUpfrontRateColumn}
                                        onChange={(v) => set({ referrerUpfrontRateColumn: v })}
                                    />
                                </Field>
                                <Field label="Referrer trail column">
                                    <Input
                                        value={form.referrerTrailRateColumn}
                                        onChange={(v) => set({ referrerTrailRateColumn: v })}
                                    />
                                </Field>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                            {savedAt && (
                                <span className="text-sm text-green-600">Saved</span>
                            )}
                            <button
                                onClick={save}
                                disabled={saving}
                                className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50"
                            >
                                {saving ? 'Saving…' : 'Save rates'}
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </>
    );
}

/** One labelled pair of upfront / trail percent inputs. */
function RateRow({ label, hint, upfront, trail, onUpfront, onTrail }) {
    return (
        <div className="grid grid-cols-[1fr,auto,auto] items-start gap-4">
            <div>
                <p className="text-sm font-medium text-slate-800">{label}</p>
                {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
            </div>
            <Field label="Upfront">
                <PctInput value={upfront} onChange={onUpfront} small />
            </Field>
            <Field label="Trail p.a.">
                <PctInput value={trail} onChange={onTrail} small />
            </Field>
        </div>
    );
}

/* ────────────────────────────  Shared UI  ───────────────────────────── */

function Toolbar({ title, onAdd, addLabel }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">{title}</h3>
            <button
                onClick={onAdd}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700"
            >
                <Plus size={15} /> {addLabel}
            </button>
        </div>
    );
}

function Card({ children }) {
    return <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">{children}</div>;
}

function RowActions({ onEdit, onDelete }) {
    return (
        <div className="flex items-center justify-end gap-1">
            <button onClick={onEdit} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md" aria-label="Edit">
                <Pencil size={15} />
            </button>
            <button onClick={onDelete} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Delete">
                <Trash2 size={15} />
            </button>
        </div>
    );
}

function Modal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
                    <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
                    <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 rounded-md" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>
                <div className="px-5 py-4 space-y-3.5">{children}</div>
            </div>
        </div>
    );
}

function ModalActions({ onCancel, onSave, saving }) {
    return (
        <div className="flex justify-end gap-2 pt-2">
            <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md">
                Cancel
            </button>
            <button onClick={onSave} disabled={saving}
                    className="px-4 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Save'}
            </button>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
            {children}
            {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
        </div>
    );
}

function Input({ value, onChange, type = 'text', autoFocus }) {
    return (
        <input
            type={type}
            value={value}
            autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-sm border border-slate-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
    );
}

function PctInput({ value, onChange, small }) {
    return (
        <div className={`relative inline-block ${small ? 'w-24' : 'w-full'}`}>
            <input
                type="number"
                step="0.0001"
                min="0"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full text-sm border border-slate-300 rounded-md pl-3 pr-6 py-1.5 text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
        </div>
    );
}

function Th({ children, align = 'left' }) {
    return (
        <th className={`px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider ${align === 'right' ? 'text-right' : 'text-left'}`}>
            {children}
        </th>
    );
}

function Td({ children, align = 'left', className = '' }) {
    return (
        <td className={`px-4 py-3 text-sm text-slate-600 ${align === 'right' ? 'text-right' : ''} ${className}`}>
            {children}
        </td>
    );
}

function Code({ children }) {
    return <span className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{children}</span>;
}

function Muted({ children }) {
    return <span className="text-slate-300">{children}</span>;
}

function TypePill({ type }) {
    const styles = type === 'SUBCONTRACTOR'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-violet-50 text-violet-700 border-violet-200';
    const label = type === 'SUBCONTRACTOR' ? 'Subcontractor' : 'Referrer';
    return <span className={`inline-flex px-2 py-0.5 text-xs font-medium border rounded-full ${styles}`}>{label}</span>;
}

function LoadingRow({ cols }) {
    return <tr><td colSpan={cols} className="px-4 py-8 text-center text-sm text-slate-400">Loading…</td></tr>;
}

function EmptyRow({ cols, label }) {
    return <tr><td colSpan={cols} className="px-4 py-8 text-center text-sm text-slate-400">{label}</td></tr>;
}

function ErrorBanner({ error }) {
    if (!error) return null;
    return (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
        </div>
    );
}

/* ─────────────────────────────  Helpers  ────────────────────────────── */

// Stored decimal fraction → percent string for display (0.007 → "0.7").
function toPct(fraction) {
    return String(Number((Number(fraction) * 100).toFixed(6)));
}
// Percent display string → decimal fraction string for the API ("0.7" → "0.007").
function fromPct(pctStr) {
    const n = Number(pctStr);
    return Number.isFinite(n) ? Number((n / 100).toFixed(8)) : 0;
}
// Stored fraction → rendered percent label ("0.70%").
function pct(fraction) {
    if (fraction == null) return '—';
    return `${Number((Number(fraction) * 100).toFixed(4))}%`;
}

function apiError(e) {
    return e?.response?.data?.message || e?.message || 'Something went wrong';
}